import React, { useState, createContext, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { Menu, X, ChevronLeft, ChevronRight, Bell, User, LogOut, Settings, HelpCircle, ChevronDown } from 'lucide-react';
import { theme } from '../tokens';
import { Avatar } from '../components/Avatar';
import { Badge } from '../components/Badge';

/**
 * DashboardLayout - Layout for authenticated dashboard pages
 * Includes collapsible sidebar, header with user menu, and main content area
 */

const DashboardContext = createContext(null);

const useDashboard = () => {
  const context = useContext(DashboardContext);
  if (!context) {
    throw new Error('Dashboard components must be used within DashboardLayout');
  }
  return context;
};

// Main wrapper
const DashboardWrapper = styled.div`
  display: flex;
  min-height: 100vh;
  background: ${theme.colors.background.secondary};
`;

// Sidebar
const SidebarWrapper = styled.aside`
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  width: ${({ $collapsed }) => ($collapsed ? '72px' : '260px')};
  background: ${theme.colors.background.primary};
  border-right: 1px solid ${theme.colors.border.light};
  transition: width ${theme.durations.normal} ${theme.easings.easeOut};
  z-index: ${theme.zIndices.sticky};
  display: flex;
  flex-direction: column;

  ${theme.media.maxMd} {
    transform: translateX(${({ $mobileOpen }) => ($mobileOpen ? '0' : '-100%')});
    width: 280px;
    box-shadow: ${({ $mobileOpen }) => ($mobileOpen ? theme.shadows['2xl'] : 'none')};
  }
`;

const SidebarHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: ${({ $collapsed }) => ($collapsed ? 'center' : 'space-between')};
  padding: ${theme.spacing[4]};
  height: 64px;
  border-bottom: 1px solid ${theme.colors.border.light};
`;

const Logo = styled(Link)`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  font-size: ${theme.fontSizes.xl};
  font-weight: ${theme.fontWeights.bold};
  color: ${theme.colors.primary[500]};
  text-decoration: none;

  ${({ $collapsed }) =>
    $collapsed &&
    css`
      span:last-child {
        display: none;
      }
    `}
`;

const CollapseButton = styled.button`
  display: none;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid ${theme.colors.border.light};
  background: ${theme.colors.background.primary};
  border-radius: ${theme.radii.md};
  color: ${theme.colors.text.secondary};
  cursor: pointer;
  transition: ${theme.transitions.button};

  &:hover {
    background: ${theme.colors.background.secondary};
    color: ${theme.colors.text.primary};
  }

  ${theme.media.lg} {
    display: flex;
  }
`;

const SidebarNav = styled.nav`
  flex: 1;
  overflow-y: auto;
  padding: ${theme.spacing[4]};
`;

const NavSection = styled.div`
  &:not(:first-child) {
    margin-top: ${theme.spacing[6]};
    padding-top: ${theme.spacing[6]};
    border-top: 1px solid ${theme.colors.border.light};
  }
`;

const NavSectionTitle = styled.div`
  font-size: ${theme.fontSizes.xs};
  font-weight: ${theme.fontWeights.semibold};
  color: ${theme.colors.text.tertiary};
  text-transform: uppercase;
  letter-spacing: ${theme.letterSpacings.wider};
  margin-bottom: ${theme.spacing[2]};
  padding: 0 ${theme.spacing[3]};

  ${({ $collapsed }) =>
    $collapsed &&
    css`
      text-align: center;
      padding: 0;
    `}
`;

// Using Link from react-router-dom for instant navigation
const navItemStyles = css`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[2.5]} ${theme.spacing[3]};
  border-radius: ${theme.radii.lg};
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
  font-weight: ${theme.fontWeights.medium};
  text-decoration: none;
  transition: background 0.15s ease, color 0.15s ease;
  cursor: pointer;

  &:hover {
    background: ${theme.colors.background.secondary};
    color: ${theme.colors.text.primary};
  }

  ${({ $active }) =>
    $active &&
    css`
      background: ${theme.colors.primary[50]};
      color: ${theme.colors.primary[600]};

      &:hover {
        background: ${theme.colors.primary[100]};
      }
    `}

  ${({ $collapsed }) =>
    $collapsed &&
    css`
      justify-content: center;
      padding: ${theme.spacing[2.5]};

      span {
        display: none;
      }
    `}

  svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }
`;

const NavItem = styled(Link)`
  ${navItemStyles}
`;

const NavItemExternal = styled.a`
  ${navItemStyles}
`;

const NavBadge = styled(Badge)`
  margin-left: auto;

  ${({ $collapsed }) =>
    $collapsed &&
    css`
      display: none;
    `}
`;

const SidebarFooter = styled.div`
  padding: ${theme.spacing[4]};
  border-top: 1px solid ${theme.colors.border.light};
`;

const FooterLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[2.5]} ${theme.spacing[3]};
  border-radius: ${theme.radii.lg};
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
  font-weight: ${theme.fontWeights.medium};
  text-decoration: none;
  transition: background 0.15s ease, color 0.15s ease;
  cursor: pointer;

  &:hover {
    background: ${theme.colors.background.secondary};
    color: ${theme.colors.text.primary};
  }

  ${({ $collapsed }) =>
    $collapsed &&
    css`
      justify-content: center;
      padding: ${theme.spacing[2.5]};

      span {
        display: none;
      }
    `}

  svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }
`;

// Main content area
const MainWrapper = styled.div`
  flex: 1;
  margin-left: ${({ $sidebarCollapsed }) => ($sidebarCollapsed ? '72px' : '260px')};
  transition: margin-left ${theme.durations.normal} ${theme.easings.easeOut};
  display: flex;
  flex-direction: column;
  min-height: 100vh;

  ${theme.media.maxMd} {
    margin-left: 0;
  }
`;

// Header
const HeaderWrapper = styled.header`
  position: sticky;
  top: 0;
  height: 64px;
  background: ${theme.colors.background.primary};
  border-bottom: 1px solid ${theme.colors.border.light};
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 ${theme.spacing[6]};
  z-index: ${theme.zIndices.sticky};
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[4]};
`;

const MobileMenuButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: none;
  background: transparent;
  color: ${theme.colors.text.secondary};
  cursor: pointer;
  border-radius: ${theme.radii.lg};

  &:hover {
    background: ${theme.colors.background.secondary};
    color: ${theme.colors.text.primary};
  }

  ${theme.media.lg} {
    display: none;
  }

  svg {
    width: 24px;
    height: 24px;
  }
`;

const PageTitle = styled.h1`
  font-size: ${theme.fontSizes.xl};
  font-weight: ${theme.fontWeights.semibold};
  color: ${theme.colors.text.primary};
  margin: 0;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

const IconButton = styled.button`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: none;
  background: transparent;
  color: ${theme.colors.text.secondary};
  cursor: pointer;
  border-radius: ${theme.radii.lg};

  &:hover {
    background: ${theme.colors.background.secondary};
    color: ${theme.colors.text.primary};
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const NotificationBadge = styled.span`
  position: absolute;
  top: 6px;
  right: 6px;
  width: 8px;
  height: 8px;
  background: ${theme.colors.error[500]};
  border-radius: ${theme.radii.full};
  border: 2px solid ${theme.colors.background.primary};
`;

const UserMenuWrapper = styled.div`
  position: relative;
`;

const UserButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[1.5]} ${theme.spacing[3]};
  border: 1px solid ${theme.colors.border.light};
  background: ${theme.colors.background.primary};
  border-radius: ${theme.radii.full};
  cursor: pointer;
  transition: border-color 0.15s ease;

  &:hover {
    border-color: ${theme.colors.border.dark};
  }

  ${theme.media.maxSm} {
    padding: ${theme.spacing[1]};
    border: none;
  }
`;

const UserName = styled.span`
  font-size: ${theme.fontSizes.sm};
  font-weight: ${theme.fontWeights.medium};
  color: ${theme.colors.text.primary};

  ${theme.media.maxSm} {
    display: none;
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 200px;
  background: ${theme.colors.background.primary};
  border: 1px solid ${theme.colors.border.light};
  border-radius: ${theme.radii.xl};
  box-shadow: ${theme.shadows.lg};
  padding: ${theme.spacing[2]};
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  visibility: ${({ $open }) => ($open ? 'visible' : 'hidden')};
  transform: translateY(${({ $open }) => ($open ? '0' : '-8px')});
  transition: opacity 0.15s ease, transform 0.15s ease, visibility 0.15s ease;
  z-index: ${theme.zIndices.dropdown};
`;

const DropdownItem = styled(Link)`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[2.5]} ${theme.spacing[3]};
  border-radius: ${theme.radii.lg};
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
  text-decoration: none;
  transition: background 0.15s ease, color 0.15s ease;

  &:hover {
    background: ${theme.colors.background.secondary};
    color: ${theme.colors.text.primary};
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const DropdownButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  width: 100%;
  padding: ${theme.spacing[2.5]} ${theme.spacing[3]};
  border-radius: ${theme.radii.lg};
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
  text-decoration: none;
  background: none;
  border: none;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;

  &:hover {
    background: ${theme.colors.error[50]};
    color: ${theme.colors.error[600]};
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const DropdownDivider = styled.div`
  height: 1px;
  background: ${theme.colors.border.light};
  margin: ${theme.spacing[2]} 0;
`;

const NotificationDropdown = styled(DropdownMenu)`
  min-width: 320px;
  right: 0;
`;

const NotificationHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border-bottom: 1px solid ${theme.colors.border.light};

  h3 {
    font-size: ${theme.fontSizes.base};
    font-weight: ${theme.fontWeights.semibold};
    margin: 0;
  }

  button {
    font-size: ${theme.fontSizes.xs};
    color: ${theme.colors.primary[500]};
    background: none;
    border: none;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const NotificationList = styled.div`
  max-height: 320px;
  overflow-y: auto;
`;

const NotificationItem = styled.div`
  display: flex;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: ${theme.colors.background.secondary};
  }
`;

const NotificationContent = styled.div`
  flex: 1;

  p {
    font-size: ${theme.fontSizes.sm};
    color: ${theme.colors.text.primary};
    margin: 0 0 ${theme.spacing[1]} 0;
  }

  span {
    font-size: ${theme.fontSizes.xs};
    color: ${theme.colors.text.tertiary};
  }
`;

const EmptyNotifications = styled.div`
  padding: ${theme.spacing[8]};
  text-align: center;
  color: ${theme.colors.text.tertiary};
  font-size: ${theme.fontSizes.sm};
`;

// Content
const ContentWrapper = styled.main`
  flex: 1;
  padding: ${theme.spacing[6]};

  ${theme.media.maxSm} {
    padding: ${theme.spacing[4]};
  }
`;

// Mobile overlay
const MobileOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: ${theme.colors.background.overlay};
  z-index: ${theme.zIndices.overlay};
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  visibility: ${({ $visible }) => ($visible ? 'visible' : 'hidden')};
  transition: opacity ${theme.durations.normal} ${theme.easings.ease},
    visibility ${theme.durations.normal} ${theme.easings.ease};

  ${theme.media.lg} {
    display: none;
  }
`;

export const DashboardLayout = ({
  children,
  logo,
  navigation = [],
  user,
  onLogout,
  headerActions,
  notifications = [],
}) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pageTitle, setPageTitle] = useState('Dashboard');
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const navigate = useNavigate();

  const contextValue = {
    sidebarCollapsed,
    setSidebarCollapsed,
    mobileOpen,
    setMobileOpen,
    pageTitle,
    setPageTitle,
  };

  const handleLogout = () => {
    setUserMenuOpen(false);
    if (onLogout) {
      onLogout();
    }
  };

  const handleNavClick = () => {
    setMobileOpen(false);
  };

  // Close dropdowns when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuOpen || notificationsOpen) {
        const isInDropdown = event.target.closest('[data-dropdown]');
        if (!isInDropdown) {
          setUserMenuOpen(false);
          setNotificationsOpen(false);
        }
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [userMenuOpen, notificationsOpen]);

  return (
    <DashboardContext.Provider value={contextValue}>
      <DashboardWrapper>
        <MobileOverlay $visible={mobileOpen} onClick={() => setMobileOpen(false)} />

        <SidebarWrapper $collapsed={sidebarCollapsed} $mobileOpen={mobileOpen}>
          <SidebarHeader $collapsed={sidebarCollapsed}>
            <Logo to="/app" $collapsed={sidebarCollapsed}>
              {logo || (
                <>
                  <span>DF</span>
                  <span>Desi Firms</span>
                </>
              )}
            </Logo>
            <CollapseButton onClick={() => setSidebarCollapsed(!sidebarCollapsed)}>
              {sidebarCollapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
            </CollapseButton>
          </SidebarHeader>

          <SidebarNav>
            {navigation.map((section, sectionIndex) => (
              <NavSection key={sectionIndex}>
                {section.title && (
                  <NavSectionTitle $collapsed={sidebarCollapsed}>
                    {sidebarCollapsed ? section.title.charAt(0) : section.title}
                  </NavSectionTitle>
                )}
                {section.items.map((item, itemIndex) =>
                  item.external ? (
                    <NavItemExternal
                      key={itemIndex}
                      href={item.href}
                      $active={item.active}
                      $collapsed={sidebarCollapsed}
                    >
                      {item.icon}
                      <span>{item.label}</span>
                      {item.badge && (
                        <NavBadge size="xs" variant="primary" $collapsed={sidebarCollapsed}>
                          {item.badge}
                        </NavBadge>
                      )}
                    </NavItemExternal>
                  ) : (
                    <NavItem
                      key={itemIndex}
                      to={item.href}
                      $active={item.active}
                      $collapsed={sidebarCollapsed}
                      onClick={handleNavClick}
                    >
                      {item.icon}
                      <span>{item.label}</span>
                      {item.badge && (
                        <NavBadge size="xs" variant="primary" $collapsed={sidebarCollapsed}>
                          {item.badge}
                        </NavBadge>
                      )}
                    </NavItem>
                  )
                )}
              </NavSection>
            ))}
          </SidebarNav>

          <SidebarFooter>
            <FooterLink to="/help" $collapsed={sidebarCollapsed}>
              <HelpCircle />
              <span>Help & Support</span>
            </FooterLink>
          </SidebarFooter>
        </SidebarWrapper>

        <MainWrapper $sidebarCollapsed={sidebarCollapsed}>
          <HeaderWrapper>
            <HeaderLeft>
              <MobileMenuButton onClick={() => setMobileOpen(true)}>
                <Menu />
              </MobileMenuButton>
              <PageTitle>{pageTitle}</PageTitle>
            </HeaderLeft>

            <HeaderRight>
              {headerActions}

              {/* Notifications */}
              <div data-dropdown style={{ position: 'relative' }}>
                <IconButton
                  aria-label="Notifications"
                  onClick={(e) => {
                    e.stopPropagation();
                    setNotificationsOpen(!notificationsOpen);
                    setUserMenuOpen(false);
                  }}
                >
                  <Bell />
                  {notifications.length > 0 && <NotificationBadge />}
                </IconButton>

                <NotificationDropdown $open={notificationsOpen}>
                  <NotificationHeader>
                    <h3>Notifications</h3>
                    {notifications.length > 0 && (
                      <button>Mark all read</button>
                    )}
                  </NotificationHeader>
                  <NotificationList>
                    {notifications.length > 0 ? (
                      notifications.map((notif, index) => (
                        <NotificationItem key={index}>
                          <NotificationContent>
                            <p>{notif.message}</p>
                            <span>{notif.time}</span>
                          </NotificationContent>
                        </NotificationItem>
                      ))
                    ) : (
                      <EmptyNotifications>
                        No new notifications
                      </EmptyNotifications>
                    )}
                  </NotificationList>
                </NotificationDropdown>
              </div>

              {/* User Menu */}
              <UserMenuWrapper data-dropdown>
                <UserButton
                  onClick={(e) => {
                    e.stopPropagation();
                    setUserMenuOpen(!userMenuOpen);
                    setNotificationsOpen(false);
                  }}
                >
                  <Avatar size="sm" src={user?.avatar} name={user?.name} />
                  <UserName>{user?.name}</UserName>
                  <ChevronDown size={16} style={{ color: theme.colors.text.tertiary }} />
                </UserButton>

                <DropdownMenu $open={userMenuOpen}>
                  <DropdownItem to="/app/profile" onClick={() => setUserMenuOpen(false)}>
                    <User />
                    My Profile
                  </DropdownItem>
                  <DropdownItem to="/app/settings" onClick={() => setUserMenuOpen(false)}>
                    <Settings />
                    Settings
                  </DropdownItem>
                  <DropdownDivider />
                  <DropdownButton onClick={handleLogout}>
                    <LogOut />
                    Logout
                  </DropdownButton>
                </DropdownMenu>
              </UserMenuWrapper>
            </HeaderRight>
          </HeaderWrapper>

          <ContentWrapper>{children}</ContentWrapper>
        </MainWrapper>
      </DashboardWrapper>
    </DashboardContext.Provider>
  );
};

// Hook for child components to update page title
DashboardLayout.usePageTitle = (title) => {
  const { setPageTitle } = useDashboard();
  React.useEffect(() => {
    setPageTitle(title);
  }, [title, setPageTitle]);
};

DashboardLayout.displayName = 'DashboardLayout';

export default DashboardLayout;
